import { withThemeParam, type NavItem, type Theme } from '@wehunt/ui'

/**
 * The shop half of the menu shared with the web shop. The shop keeps its own
 * copy of the same list (crossAppMenu.ts there), so a change here is a change
 * there too; the contract is the menu table in
 * docs/superpowers/specs/2026-09-10-cross-app-menu-design.md.
 *
 * Each enabled item points at a stable entry route on the shop, which resolves
 * the member's localized account page and signs a guest in on the way. WeHunt
 * Web never needs to know the shop's API, its CMS paths or its cookie state.
 *
 * Icons are the design's: the account dashboard is the account glyph (not the
 * grid that Overview uses), and the rest are the plain Material outlines the
 * prototype draws. "warranties" is listed but disabled — the shop has no page
 * for it yet — so both apps show the same menu, greyed out in the same place.
 */
export const SHOP_ACCOUNT_ITEMS = [
  { id: 'dashboard', icon: 'account', enabled: true },
  { id: 'products', icon: 'products', enabled: true },
  { id: 'subscriptions', icon: 'subscriptions', enabled: true },
  { id: 'orders', icon: 'orders', enabled: true },
  { id: 'address', icon: 'address', enabled: true },
  { id: 'warranties', icon: 'warranty', enabled: false },
  { id: 'wallet', icon: 'wallet', enabled: true },
] as const

export type ShopAccountItemId = (typeof SHOP_ACCOUNT_ITEMS)[number]['id']

export interface ShopLinks {
  shopUrl: string
  shopUrlNo: string
}

/**
 * Values arrive as strings from envsubst: an unset variable is "" and a
 * variable missing from the envsubst list is the literal "${SHOP_URL}". Both
 * mean "not configured" — a dead link is worse than no link.
 */
function readConfiguredUrl(raw: unknown): string {
  if (typeof raw !== 'string') return ''
  const trimmed = raw.trim()
  if (trimmed.startsWith('${')) return ''
  return trimmed.replace(/\/+$/, '')
}

export function readShopLinks(): ShopLinks {
  // Live from window rather than the appConfig snapshot, matching
  // isFeatureEnabled: the snapshot cannot be varied in a test.
  const raw = window.__APP_CONFIG__?.links
  return { shopUrl: readConfiguredUrl(raw?.shopUrl), shopUrlNo: readConfiguredUrl(raw?.shopUrlNo) }
}

/**
 * Norwegian in any spelling: the macro code `no`, Bokmål `nb`, Nynorsk `nn`,
 * with or without a region. WeHunt Web knows only the UI language, not the
 * member's country; this is the agreed proxy for "shops at wehunt.no".
 */
export function isNorwegian(language: string | undefined): boolean {
  return /^(no|nb|nn)(?:[-_]|$)/i.test(language ?? '')
}

export function shopBaseUrl(links: ShopLinks, language: string | undefined): string {
  if (!links.shopUrl) return ''
  return isNorwegian(language) && links.shopUrlNo ? links.shopUrlNo : links.shopUrl
}

export interface ShopNavOptions {
  /**
   * The member's light/dark choice, carried into the shop as `?theme=` so it
   * arrives already in the right mode (the shop hands it back the same way).
   */
  theme?: Theme
  /** Tooltip on the disabled rows, e.g. "Coming soon". */
  comingSoonLabel?: string
}

export function buildShopNavItems(
  links: ShopLinks,
  language: string | undefined,
  label: (id: ShopAccountItemId) => string,
  { theme, comingSoonLabel }: ShopNavOptions = {},
): NavItem[] {
  const base = shopBaseUrl(links, language)
  if (!base) return []
  return SHOP_ACCOUNT_ITEMS.map(({ id, icon, enabled }) => {
    if (!enabled) {
      return { id, icon, label: label(id), disabled: true, disabledTitle: comingSoonLabel }
    }
    const href = `${base}/go/account/${id}`
    return {
      id,
      icon,
      label: label(id),
      href: theme ? withThemeParam(href, theme) : href,
      external: true,
    }
  })
}

/**
 * The top bar's two shop controls. Like the account items they go through the
 * shop's stable `/go/…` entry routes, which add the visitor's locale on
 * arrival — this app cannot know which localized cart or storefront URL the
 * CMS has given the member's market.
 *
 * The cart lives in the shop and this app cannot read it, so the icon is a
 * plain link to the shop's cart page; search opens the storefront with the
 * term already in its search overlay (`?q=`), where the shop's own index
 * answers it.
 */
export const SHOP_SEARCH_PARAM = 'q'

export function shopCartHref(base: string, theme?: Theme): string {
  const href = `${base}/go/cart`
  return theme ? withThemeParam(href, theme) : href
}

export function shopSearchHref(base: string, query: string, theme?: Theme): string {
  const params = new URLSearchParams({ [SHOP_SEARCH_PARAM]: query.trim() })
  const href = `${base}/go/shop?${params.toString()}`
  return theme ? withThemeParam(href, theme) : href
}
